import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _UserEnrolledSkeleton, _InductionBanner, _UserWithoutEnrollmen;
import { useCallback, useEffect } from "react";
import uniqBy from "lodash/uniqBy";
import { createUrlWithSourceQueryParams } from "@crehana/analytics";
import { ApprovalRequirementEnum } from "../../../../__generated__/graphql-schema";
import InductionBanner from "../../../components/InductionBanner";
import EnrollmentsErrorMessage from "../../components/EnrollmentsErrorMessage";
import { useCanUseInductionQuery, useCourseEnrollments, useLazyGetMentors, useLearnLastActivityContext } from "../../hooks";
import { isCourseCanUploadProject, isCourseElevate, isCourseWithQuiz } from "../../utils";
import useOrganizationCourses from "../../hooks/useOrganizationCourses";
import UserElevateCourse from "./sections/UserElevateCourse";
import UserEnrolled from "./sections/UserEnrolled";
import UserEnrolledSkeleton from "./sections/UserEnrolled/components/UserEnrolledSkeleton";
import UserUniqueCourseEmptyMentors from "./sections/UserUniqueCourseEmptyMentors";
import UserWithoutEnrollment from "./sections/UserWithoutEnrollment";
import UserWithoutMentorsAndProjects from "./sections/UserWithoutMentorsAndProjects";
import { jsx as _jsx } from "react/jsx-runtime";
var StudentRoles = {
  INDUCTION: 'induction',
  MEMBER: 'member',
  ADMIN: 'admin'
};
var B2BLayout = function B2BLayout() {
  var _me$organization, _me$organization2, _mostRecentCourse$stu, _mostRecentCourse$cat2;
  var _useLearnLastActivity = useLearnLastActivityContext(),
    me = _useLearnLastActivity.me,
    isDark = _useLearnLastActivity.isDark,
    source = _useLearnLastActivity.source,
    sourceDetail = _useLearnLastActivity.sourceDetail,
    containerType = _useLearnLastActivity.containerType,
    hasEnrollments = _useLearnLastActivity.hasEnrollments,
    mostRecentCourse = _useLearnLastActivity.mostRecentCourse,
    playroomQuizCoinValue = _useLearnLastActivity.playroomQuizCoinValue,
    hideSliderNewCourses = _useLearnLastActivity.hideSliderNewCourses,
    setMentors = _useLearnLastActivity.setMentors,
    onGoToQuiz = _useLearnLastActivity.onGoToQuiz,
    onGoToContentForYou = _useLearnLastActivity.onGoToContentForYou;
  var organizationSlug = (_me$organization = me.organization) === null || _me$organization === void 0 ? void 0 : _me$organization.slug;
  var isStudentInInduction = ((_me$organization2 = me.organization) === null || _me$organization2 === void 0 ? void 0 : _me$organization2.myRole) === StudentRoles.INDUCTION;
  var inductionQuery = useCanUseInductionQuery({
    orgSlug: organizationSlug
  });
  var _useCourseEnrollments = useCourseEnrollments({
      student: 'b2b',
      skip: inductionQuery.loading
    }),
    courseEnrollments = _useCourseEnrollments.courseEnrollments,
    loading = _useCourseEnrollments.loading,
    error = _useCourseEnrollments.error,
    onRefetch = _useCourseEnrollments.onRefetch;
  var _useLazyGetMentors = useLazyGetMentors(),
    mentorList = _useLazyGetMentors.mentors,
    loadingMentors = _useLazyGetMentors.loading,
    errorMentors = _useLazyGetMentors.error,
    fetchMentors = _useLazyGetMentors.fetchMentors;
  var _useOrganizationCours = useOrganizationCourses({
      organizationSlug: organizationSlug,
      skip: isStudentInInduction
    }),
    organizationCourses = _useOrganizationCours.organizationCourses,
    loadingOrganizationCourses = _useOrganizationCours.loading;
  var mergedCourses = uniqBy([].concat(_toConsumableArray(courseEnrollments), _toConsumableArray(organizationCourses)).filter(Boolean), function (course) {
    return course.originalId;
  }).filter(function (course) {
    return course.originalId !== (mostRecentCourse === null || mostRecentCourse === void 0 ? void 0 : mostRecentCourse.originalId);
  });
  var isMostRecentCourseCanUploadProject = !!mostRecentCourse && isCourseCanUploadProject(mostRecentCourse);
  var isQuizAvailable = !!mostRecentCourse && isCourseWithQuiz(mostRecentCourse);
  var isMostRecentCourseWithQuizOrProject = isQuizAvailable || isMostRecentCourseCanUploadProject;
  var hasError = !!error || errorMentors;
  var hasAllProjectsEmpty = mergedCourses.length > 0 && mergedCourses.some(function (enrollment) {
    var _enrollment$permissio;
    return !!((_enrollment$permissio = enrollment.permissionsV2) !== null && _enrollment$permissio !== void 0 && _enrollment$permissio.canUploadProject) && enrollment.project === undefined;
  });

  // El curso de most recent es de Elevate y tiene quiz
  var shouldRenderWhenElevate = !!mostRecentCourse && isCourseElevate(mostRecentCourse) && isMostRecentCourseWithQuizOrProject;

  // User don't have any mentors and projects
  var shouldRenderUserWithoutMentorsAndProjects = (hasAllProjectsEmpty && !shouldRenderWhenElevate || !isMostRecentCourseWithQuizOrProject) && mergedCourses.length > 0;

  // Solo hay un curso sin proyecto y sin mentores
  var shouldRenderUserWithoutMentors = mergedCourses.length === 1 && mergedCourses[0].project === undefined && (mostRecentCourse === null || mostRecentCourse === void 0 ? void 0 : (_mostRecentCourse$stu = mostRecentCourse.studioConfigSet) === null || _mostRecentCourse$stu === void 0 ? void 0 : _mostRecentCourse$stu.approvalRequirementEnum) !== ApprovalRequirementEnum.QUIZ_EVALUATION && mentorList.length === 0 && !shouldRenderWhenElevate && !isMostRecentCourseWithQuizOrProject;
  var goToContentForYou = useCallback(function () {
    if (typeof onGoToContentForYou === 'function') {
      onGoToContentForYou();
    } else {
      var _me$organization3;
      window.location.href = createUrlWithSourceQueryParams({
        url: "/org/".concat((_me$organization3 = me.organization) === null || _me$organization3 === void 0 ? void 0 : _me$organization3.slug, "/learn/content-for-you/"),
        source: source
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onGoToContentForYou]);
  useEffect(function () {
    if (mostRecentCourse) {
      var _mostRecentCourse$cat;
      var subCategoryId = mostRecentCourse !== null && mostRecentCourse !== void 0 && mostRecentCourse.catalogSubCategories ? mostRecentCourse === null || mostRecentCourse === void 0 ? void 0 : (_mostRecentCourse$cat = mostRecentCourse.catalogSubCategories[0]) === null || _mostRecentCourse$cat === void 0 ? void 0 : _mostRecentCourse$cat.originalId : undefined;
      fetchMentors(subCategoryId || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostRecentCourse === null || mostRecentCourse === void 0 ? void 0 : (_mostRecentCourse$cat2 = mostRecentCourse.catalogSubCategories) === null || _mostRecentCourse$cat2 === void 0 ? void 0 : _mostRecentCourse$cat2.length]);
  useEffect(function () {
    if (!loadingMentors) {
      setMentors(mentorList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMentors, mentorList.length]);
  var isLoading = loading || inductionQuery.loading || loadingMentors || loadingOrganizationCourses;
  if (isLoading) {
    return _UserEnrolledSkeleton || (_UserEnrolledSkeleton = /*#__PURE__*/_jsx(UserEnrolledSkeleton, {
      "data-testid": "user-enrolled-skeleton",
      withContainer: true
    }));
  }
  if (isStudentInInduction) {
    return _InductionBanner || (_InductionBanner = /*#__PURE__*/_jsx(InductionBanner, {}));
  }
  if (hasError) {
    return /*#__PURE__*/_jsx(EnrollmentsErrorMessage, {
      onRetry: onRefetch,
      withContainer: true,
      "data-testid": "my-courses-error-message"
    });
  }
  if (shouldRenderUserWithoutMentors) {
    return /*#__PURE__*/_jsx(UserUniqueCourseEmptyMentors, {
      me: me,
      isDark: isDark,
      goToContentForYou: goToContentForYou,
      containerType: containerType
    });
  }
  if (shouldRenderWhenElevate) {
    return /*#__PURE__*/_jsx(UserElevateCourse, {
      isDark: isDark,
      courseEnrollments: mergedCourses,
      containerType: containerType,
      mostRecentCourse: mostRecentCourse,
      playroomQuizCoinValue: playroomQuizCoinValue,
      onGoToQuiz: onGoToQuiz,
      amplitude: {
        source: source,
        sourceDetail: sourceDetail
      }
    });
  }
  if (shouldRenderUserWithoutMentorsAndProjects) {
    return /*#__PURE__*/_jsx(UserWithoutMentorsAndProjects, {
      containerType: containerType,
      courses: mergedCourses,
      isDark: isDark
    });
  }
  if (hasEnrollments) {
    return /*#__PURE__*/_jsx(UserEnrolled, {
      isDark: isDark,
      containerType: containerType
    });
  }
  if (!hideSliderNewCourses) {
    return _UserWithoutEnrollmen || (_UserWithoutEnrollmen = /*#__PURE__*/_jsx(UserWithoutEnrollment, {}));
  }
  return null;
};
export default B2BLayout;